import React from 'react';
import { inject, observer } from "mobx-react";

const COVER_WEBP_PATH = '/public/images/CoversVKg-webp/';
const COVER_WIDTHS = [160, 320, 640];

// Een sku die via string-concatenatie "undefined"/"null" werd, is geen sku:
// anders vraagt de fallback-reeks 8 niet-bestaande bestanden op (/undefined.webp, ...).
export function hasValidCoverSku(sku) {
    if (sku === undefined || sku === null) return false;
    const value = String(sku).trim();
    return value !== '' && value !== '0' && value !== 'undefined' && value !== 'null';
}

export function getBookCoverThumbnailUrl(sku, urlPublic, width = 320) {
    if (!hasValidCoverSku(sku) || !urlPublic) return null;
    return `${urlPublic}/images/thumbnail.php?kind=cover&name=${encodeURIComponent(sku)}&width=${width}`;
}
const NOT_AVAILABLE_PATH = '/public/images/img-not-available.png';
const COVER_FALLBACKS = [
    { path: COVER_WEBP_PATH, ext: 'webp' },
    { path: '/public/images/CoversVKg-all/', ext: 'png' },
    { path: '/public/images/CoversVKg-all/', ext: 'jpg' },
    { path: '/public/images/CoversVKg/', ext: 'png' },
    { path: '/public/images/CoversVKg/', ext: 'jpg' },
    { path: '/public/images/CoversVK/', ext: 'jpg' },
    { path: '/public/images/Covers/', ext: 'jpg' }
];

export function getBookCoverWebpUrl(sku, urlPublic) {
    if (!hasValidCoverSku(sku) || !urlPublic) {
        return null;
    }
    return urlPublic + COVER_WEBP_PATH + sku + '.webp';
}

export function getBookCoverUrls(sku, urlPublic) {
    if (!hasValidCoverSku(sku) || !urlPublic) {
        return [];
    }

    return COVER_FALLBACKS.map(({ path, ext }) => (
        urlPublic + path + sku + '.' + ext
    ));
}

function getInitialCoverState(sku, urlPublic) {
    if (process.env.NODE_ENV === 'development' && sku !== undefined && !hasValidCoverSku(sku)) {
        console.warn('[ImgCover] ongeldige sku, toont placeholder:', JSON.stringify(sku));
    }
    const coverUrls = getBookCoverUrls(sku, urlPublic);
    const hasCoverUrls = coverUrls.length > 0;

    return {
        current: 0,
        thumbnail: hasCoverUrls,
        image: hasCoverUrls ? getBookCoverThumbnailUrl(sku, urlPublic) : (urlPublic ? urlPublic + NOT_AVAILABLE_PATH : null),
        not_available: !hasCoverUrls,
        title: null,
        ratio: null
    };
}

const ImgCover = inject("stores") (
    observer (
        class ImgCover extends React.PureComponent {

            constructor(props) {
                super(props);
                this.urlPublic = process.env.REACT_APP_URL_PUBLIC;

                this.state = getInitialCoverState(props.sku, this.urlPublic);
            }

            componentDidUpdate(prevProps) {
                if (prevProps.sku !== this.props.sku) {
                    this.setState(getInitialCoverState(this.props.sku, this.urlPublic));
                }
            }

            // Covers zijn niet allemaal 2:3. Het frame (met rand) neemt de echte
            // verhouding over, zodat de rand precies om de cover sluit.
            handleLoad = (e) => {
                const { naturalWidth, naturalHeight } = e.target;
                if (naturalWidth > 0 && naturalHeight > 0) {
                    const ratio = Math.round(naturalWidth / naturalHeight * 10000) / 10000;
                    if (ratio !== this.state.ratio) this.setState({ ratio });
                }
            }

            handleError = () => {
                const { current } = this.state;
                const { sku } = this.props;
                const coverUrls = getBookCoverUrls(sku, this.urlPublic);

                if (this.state.not_available) {
                    return;
                }

                if (current >= 0 && current < coverUrls.length) {
                    this.setState({
                        image: coverUrls[current],
                        thumbnail: false,
                        current: current + 1,
                        not_available: false,
                        ratio: null
                    });
                } else {
                    this.setState({
                        image: this.urlPublic + NOT_AVAILABLE_PATH,
                        thumbnail: false,
                        title: this.props.title,
                        not_available: true,
                        ratio: null
                    });
                }
            }

            render() {
                const aspectRatio = this.props.aspect ? ' ' + this.props.aspect : '';
                const priority = Boolean(this.props.priority);
                const alt = this.props.title ? this.props.title : this.props.sku;

                return (
                    <div
                        className={'img-cover__frame' + (priority ? ' img-cover__frame--priority' : '')}
                        style={this.state.ratio ? { '--cover-ratio': this.state.ratio } : undefined}
                    >
                        <img
                            onLoad={this.handleLoad}
                            onError={this.handleError}
                            src={this.state.image || undefined}
                            srcSet={this.state.thumbnail ? COVER_WIDTHS.map(width =>
                                `${getBookCoverThumbnailUrl(this.props.sku, this.urlPublic, width)} ${width}w`
                            ).join(', ') : undefined}
                            sizes={this.state.thumbnail ? (this.props.sizes || '160px') : undefined}
                            className={'cover' + aspectRatio + (this.props.soldout ? ' --soldout' : '')}
                            alt={alt}
                            width="160"
                            height="240"
                            loading={priority ? 'eager' : 'lazy'}
                            fetchpriority={priority ? 'high' : 'auto'}
                            decoding={priority ? 'sync' : 'async'}
                        />

                        {this.state.not_available &&
                            <div className="cover-title--not-available">
                                {this.props.title &&
                                    <React.Fragment>
                                        <span className="cover-title--not-available__caption">Titel:</span>
                                        <span className="cover-title--not-available__txt" dangerouslySetInnerHTML={{ __html: this.state.title }} />
                                    </React.Fragment>
                                }
                            </div>
                        }
                    </div>
                );
            }
        }
    )
);

export default ImgCover;
